import { useMemo } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useNation } from '@/hooks/useNation'
import { useWorld } from '@/hooks/useWorld'
import { useRegionsByNation } from '@/hooks/useRegionsByNation'
import { usePartiesByNation } from '@/hooks/usePartiesByNation'
import { useCanCreateParty } from '@/hooks/useCanCreateParty'
import { useMyPartyInNation } from '@/hooks/useMyPartyInNation'
import { AppLayout } from '@/components/layout/AppLayout'
import { AdminOnly } from '@/components/AdminOnly'
import { BulkGenerateCohortsButton } from '@/components/cohort/BulkGenerateCohortsButton'
import { RegionCard } from '@/components/region/RegionCard'
import { RegionCardSkeleton } from '@/components/region/RegionCardSkeleton'
import { PartyCard } from '@/components/party/PartyCard'
import { PartyCardSkeleton } from '@/components/party/PartyCardSkeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Users, Eye } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { BreadcrumbItem } from '@/components/layout/Breadcrumb'
import type { GovernmentType, ElectoralSystem, JudicialReviewStrength, MediaFreedom } from '@/types'

const governmentLabels: Record<GovernmentType, string> = {
  parliamentary: 'Parliamentary',
  semi_presidential: 'Semi-Presidential',
  presidential: 'Presidential',
}

const electoralLabels: Record<ElectoralSystem, string> = {
  nationwide_pr: 'Nationwide PR',
  regional_pr: 'Regional PR',
  mixed: 'Mixed',
}

const judicialLabels: Record<JudicialReviewStrength, string> = {
  none: 'None',
  weak: 'Weak',
  strong: 'Strong',
}

const mediaLabels: Record<MediaFreedom, string> = {
  free: 'Free',
  state_influenced: 'State Influenced',
  polarized: 'Polarized',
  censored: 'Censored',
}

function formatTermLength(ticks: number): string {
  return `${ticks} ticks (${ticks} days)`
}

export default function NationDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { nation, isLoading: nationLoading, error: nationError } = useNation(id)
  const { world, isLoading: worldLoading } = useWorld(nation?.world_id)
  const { regions, isLoading: regionsLoading, error: regionsError } = useRegionsByNation(id)
  const { parties, isLoading: partiesLoading, error: partiesError } = usePartiesByNation(id)
  const { canCreate, isLoading: canCreateLoading } = useCanCreateParty(id)
  const { party: myParty } = useMyPartyInNation(id)

  const isLoading = nationLoading
  const error = nationError

  const activeParties = parties?.filter((p) => p.is_active) || []

  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [{ label: 'Worlds', href: '/worlds' }]
    if (world) {
      items.push({ label: world.name, href: `/worlds/${world.id}` })
    }
    if (nation) {
      items.push({ label: nation.name })
    }
    return items
  }, [world, nation])

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          {nationError?.message === 'JSON object requested, multiple (or no) rows returned'
            ? 'Nation not found.'
            : 'Failed to load nation. Please try again later.'}
        </div>
      )}

      {(isLoading || worldLoading) && (
        <div className="space-y-6">
          <div className="bg-muted h-8 w-48 animate-pulse rounded" />
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <div className="bg-muted h-5 w-32 animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="bg-muted h-4 w-40 animate-pulse rounded" />
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <div className="bg-muted h-5 w-28 animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[1, 2].map((i) => (
                    <div key={i} className="bg-muted h-4 w-36 animate-pulse rounded" />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {!isLoading && !worldLoading && !error && nation && (
        <>
          <div className="mb-8 flex items-center justify-between">
            <h1 className="text-3xl font-bold">{nation.name}</h1>
            <div className="flex items-center gap-2">
              <Button variant="outline" asChild>
                <Link to={`/nations/${nation.id}/overview`}>
                  <Eye className="mr-2 h-4 w-4" />
                  Public Overview
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to={`/nations/${nation.id}/demographics`}>
                  <Users className="mr-2 h-4 w-4" />
                  Voter Demographics
                </Link>
              </Button>
              <BulkGenerateCohortsButton nationId={nation.id} nationName={nation.name} />
            </div>
          </div>

          <div className="mb-8 grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Government Structure</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Type</dt>
                    <dd className="font-medium">{governmentLabels[nation.government_type]}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Electoral System</dt>
                    <dd className="font-medium">{electoralLabels[nation.electoral_system]}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Term Length</dt>
                    <dd className="font-medium">{formatTermLength(nation.term_length_ticks)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Confidence Votes</dt>
                    <dd className="font-medium">
                      {nation.confidence_votes_enabled ? 'Yes' : 'No'}
                    </dd>
                  </div>
                </dl>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Electoral Rules</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Threshold</dt>
                    <dd className="font-medium">{nation.threshold_percent}%</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Max Parties</dt>
                    <dd className="font-medium">{nation.max_parties}</dd>
                  </div>
                </dl>
              </CardContent>
            </Card>
          </div>

          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-lg">Institutions</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid gap-4 text-sm sm:grid-cols-2">
                <div className="flex justify-between sm:flex-col sm:justify-start">
                  <dt className="text-muted-foreground">Judicial Review Strength</dt>
                  <dd className="font-medium sm:mt-1">
                    {judicialLabels[nation.judicial_review_strength]}
                  </dd>
                </div>
                <div className="flex justify-between sm:flex-col sm:justify-start">
                  <dt className="text-muted-foreground">Media Freedom</dt>
                  <dd className="font-medium sm:mt-1">{mediaLabels[nation.media_freedom]}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          {/* Parties Section */}
          <div className="mb-8 border-t pt-6">
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold">Political Parties</h2>
                <Badge variant="outline">
                  {activeParties.length}/{nation.max_parties}
                </Badge>
              </div>
              {!canCreateLoading && canCreate?.allowed && (
                <Button onClick={() => navigate(`/nations/${id}/create-party`)}>
                  Create Party
                </Button>
              )}
              {!canCreateLoading && myParty && (
                <Button variant="outline" onClick={() => navigate(`/parties/${myParty.id}`)}>
                  View Your Party
                </Button>
              )}
            </div>

            {partiesError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
                Failed to load parties. Please try again later.
              </div>
            )}

            {partiesLoading && (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3].map((i) => (
                  <PartyCardSkeleton key={i} />
                ))}
              </div>
            )}

            {!partiesLoading && !partiesError && activeParties.length === 0 && (
              <div className="rounded-lg border border-dashed p-12 text-center">
                <h3 className="text-lg font-medium">No parties yet</h3>
                <p className="text-muted-foreground mt-1">
                  Be the first to create a political party in this nation!
                </p>
                {!canCreateLoading && canCreate?.allowed && (
                  <Button className="mt-4" onClick={() => navigate(`/nations/${id}/create-party`)}>
                    Create a Party
                  </Button>
                )}
              </div>
            )}

            {!partiesLoading && !partiesError && activeParties.length > 0 && (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {activeParties.map((party) => (
                  <PartyCard
                    key={party.id}
                    party={party}
                    isUserParty={myParty?.id === party.id}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="border-t pt-6">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-semibold">Regions</h2>
              <AdminOnly>
                <Button onClick={() => navigate(`/nations/${id}/regions/new`)}>Add Region</Button>
              </AdminOnly>
            </div>

            {regionsError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
                Failed to load regions. Please try again later.
              </div>
            )}

            {regionsLoading && (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3].map((i) => (
                  <RegionCardSkeleton key={i} />
                ))}
              </div>
            )}

            {!regionsLoading && !regionsError && regions && regions.length === 0 && (
              <div className="rounded-lg border border-dashed p-12 text-center">
                <h3 className="text-lg font-medium">No regions yet</h3>
                <p className="text-muted-foreground mt-1">
                  This nation doesn't have any regions yet.
                </p>
                <AdminOnly>
                  <Button className="mt-4" onClick={() => navigate(`/nations/${id}/regions/new`)}>
                    Create the first region
                  </Button>
                </AdminOnly>
              </div>
            )}

            {!regionsLoading && !regionsError && regions && regions.length > 0 && (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {regions.map((region) => (
                  <RegionCard key={region.id} region={region} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </AppLayout>
  )
}
